<template>
  <div class="user-edit-page">
    <b-card no-body class="p-2">
      <div v-if="isLoading" class="text-center py-3">
        <b-spinner label="Loading user..." />
      </div>
      <b-alert v-else-if="loadError" show variant="danger">
        {{ loadError }}
        <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchUser">Retry</b-button>
      </b-alert>
      <validation-observer v-else ref="formObserver">
        <b-form @submit.prevent="saveUser">
          <b-row>
            <b-col md="6">
              <mypoda-form-field
                label="Name"
                name="Name"
                rules="required|max:255"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="form.name"
                    :state="state"
                    placeholder="Full name"
                  />
                </template>
              </mypoda-form-field>
            </b-col>
            <b-col md="6">
              <mypoda-form-field
                label="Email"
                name="Email"
                rules="email"
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="form.email"
                    type="email"
                    :state="state"
                    placeholder="user@example.com"
                  />
                </template>
              </mypoda-form-field>
            </b-col>
            <b-col md="6">
              <mypoda-form-field
                label="Phone"
                name="Phone"
                rules="phone_10"
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    :value="form.phone"
                    :state="state"
                    inputmode="numeric"
                    maxlength="10"
                    placeholder="10-digit phone number"
                    @input="onPhoneInput"
                  />
                </template>
              </mypoda-form-field>
            </b-col>
            <b-col md="6">
              <mypoda-form-field
                label="Date of birth"
                name="Date of birth"
              >
                <template #default="{ state }">
                  <admin-date-picker
                    v-model="form.dob"
                    :class="state === false ? 'is-invalid' : ''"
                    placeholder="MM/DD/YYYY"
                  />
                </template>
              </mypoda-form-field>
            </b-col>
            <b-col md="12">
              <b-form-group label="Selected category">
                <b-form-input
                  :value="form.selectedCategory"
                  readonly
                  placeholder="No category selected"
                />
              </b-form-group>
            </b-col>
            <b-col md="12" class="d-flex justify-content-end">
              <b-button type="button" variant="outline-secondary" class="mr-1" @click="goBack">
                Cancel
              </b-button>
              <b-button type="submit" variant="primary" :disabled="isSaving">
                <b-spinner v-if="isSaving" small class="mr-50" />
                Save changes
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { adminPanelApi } from '@/services/adminPanelApi'
import AdminDatePicker from '@/components/admin/AdminDatePicker.vue'
import MypodaFormField from '@/components/mypoda/MypodaFormField.vue'
import { formatDobForApi, formatDobForDisplay, unwrapAdminResponse } from '@/utils/adminApiHelpers'
import { notifyError, notifySuccess } from '@/utils/uiActions'

export default {
  name: 'UserEdit',
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BSpinner,
    ValidationObserver,
    AdminDatePicker,
    MypodaFormField,
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      loadError: '',
      form: {
        name: '',
        email: '',
        phone: '',
        dob: '',
        selectedCategory: '',
      },
    }
  },
  computed: {
    userId() {
      return this.$route.params.id
    },
  },
  mounted() {
    this.fetchUser()
  },
  methods: {
    onPhoneInput(value) {
      this.form.phone = String(value || '').replace(/\D/g, '').slice(0, 10)
    },
    async fetchUser() {
      if (!this.userId) return
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.userDetails(this.userId)
        const profile = unwrapAdminResponse(res)
        this.form = {
          name: profile.name || profile.username || '',
          email: profile.email || '',
          phone: String(profile.phone || profile.contact || '').replace(/\D/g, '').slice(0, 10),
          dob: formatDobForDisplay(profile.dob_raw || profile.dob || ''),
          selectedCategory: profile.selected_category || '',
        }
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load user.'
      } finally {
        this.isLoading = false
      }
    },
    async saveUser() {
      const valid = await this.$refs.formObserver.validate()
      if (!valid) return

      this.isSaving = true
      try {
        await adminPanelApi.updateUser(this.userId, {
          name: this.form.name.trim(),
          email: this.form.email.trim() || null,
          phone: this.form.phone.trim() || null,
          dob: formatDobForApi(this.form.dob),
        })
        notifySuccess(this, 'User updated successfully.')
        this.goBack()
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to save user.')
      } finally {
        this.isSaving = false
      }
    },
    goBack() {
      this.$router.push({ name: 'clientmanagement' }).catch(() => {})
    },
  },
}
</script>
